/**
 * Overfitting Analysis Mock
 *
 * Mirrors the WFAOverfittingAnalysis component — shows four metrics
 * measuring the gap between in-sample and out-of-sample performance.
 */

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowDown, faArrowUp } from '@fortawesome/free-solid-svg-icons';

interface Metric {
  label: string;
  value: string;
  direction: 'drop' | 'hold';
  severity: 'good' | 'warning' | 'danger';
  description: string;
}

const metrics: Metric[] = [
  {
    label: 'Performance Drop',
    value: '45.3%',
    direction: 'drop',
    severity: 'warning',
    description: 'OOS return vs IS return',
  },
  {
    label: 'Sharpe Drop',
    value: '0.58',
    direction: 'drop',
    severity: 'warning',
    description: 'Points lost out-of-sample',
  },
  {
    label: 'Win Rate Drop',
    value: '3.2%',
    direction: 'drop',
    severity: 'good',
    description: 'Minimal degradation',
  },
  {
    label: 'Consistency',
    value: '72.4%',
    direction: 'hold',
    severity: 'good',
    description: 'Cross-window stability',
  },
];

const severityColor = (s: Metric['severity']) => {
  if (s === 'good') return '#22c55e';
  if (s === 'warning') return '#f59e0b';
  return '#ef4444';
};

export default function OverfittingAnalysisMock() {
  return (
    <div className="platform-mock">
      <div className="platform-mock-header">
        <span className="dot" /> Overfitting Analysis
      </div>
      <div className="platform-mock-body" style={{ padding: '0.75rem' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0.5rem' }}>
          {metrics.map((m) => {
            const color = severityColor(m.severity);
            return (
              <div key={m.label} style={{
                background: '#0c1520',
                border: '1px solid rgba(255,255,255,0.06)',
                borderRadius: '0.5rem',
                padding: '0.625rem',
                display: 'flex',
                flexDirection: 'column',
                gap: '0.25rem',
              }}>
                <div style={{ fontSize: '0.5rem', color: '#6b7280', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                  {m.label}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                  <span style={{ fontSize: '0.875rem', fontWeight: 700, color, fontFamily: 'ui-monospace, monospace' }}>
                    {m.value}
                  </span>
                  {m.direction === 'drop' && (
                    <FontAwesomeIcon icon={faArrowDown} style={{ fontSize: '0.5rem', color }} />
                  )}
                  {m.direction === 'hold' && (
                    <FontAwesomeIcon icon={faArrowUp} style={{ fontSize: '0.5rem', color }} />
                  )}
                </div>
                <div style={{ fontSize: '0.5rem', color: '#6b7280' }}>
                  {m.description}
                </div>
              </div>
            );
          })}
        </div>

        {/* Interpretation */}
        <div style={{
          marginTop: '0.625rem',
          background: 'rgba(245,158,11,0.08)',
          border: '1px solid rgba(245,158,11,0.2)',
          borderRadius: '0.375rem',
          padding: '0.5rem 0.625rem',
          fontSize: '0.5625rem',
          color: '#9ca3af',
        }}>
          <span style={{ color: '#fcd34d', fontWeight: 600 }}>Moderate degradation:</span> Some performance lost out-of-sample is normal. Consistency above 70% suggests an edge, but the Sharpe drop warrants stability testing.
        </div>
      </div>
      <div className="platform-mock-caption">
        Overfitting analysis compares in-sample promises against out-of-sample reality across all walk-forward windows.
      </div>
    </div>
  );
}
